import { useEditModeState } from '@projects/edit-modes';
import { externalDataDefaultColor, getLayingTypeColor } from '@trenches/colors';
import { DeepupCatmullRomCurve } from '@trenches/components/Trench/DeepupCatmullRomCurve';
import type { LayingType, Point } from '@trenches/types';
import { useThree } from '@react-three/fiber';
import { getCachedMaterial, getRenderOrder } from '@viewer3D/helper';
import { Object3dNames } from '@viewer3D/types';
import { useEffect, useMemo } from 'react';
import { TubeGeometry } from 'three';
import { useExternalDataState, useExternalDataTrenches } from './hooks';

const LineString = ({ points, layingType }: { points: Point[]; layingType: LayingType }) => {
  const { isAttributesMode } = useEditModeState();
  const color = isAttributesMode ? getLayingTypeColor(layingType) : externalDataDefaultColor;

  const pointsKey = JSON.stringify(points);

  const { curve, geometry } = useMemo(() => {
    const c = new DeepupCatmullRomCurve();
    c.updatePoints(points as Point[]);
    const g = new TubeGeometry(c, points.length, 0.15, 8, false);
    return { curve: c, geometry: g };
  }, [pointsKey, points.length]);

  useEffect(() => {
    return () => {
      geometry.dispose();
    };
  }, [geometry]);

  return (
    <group position={curve.worldPosition} name={Object3dNames.ExternalDataTrenchSegments}>
      <mesh geometry={geometry} material={getCachedMaterial(color, 0.85)} frustumCulled={false} />
    </group>
  );
};

export const ExternalDataTrenches = () => {
  const { trenches } = useExternalDataTrenches();

  if (!trenches.length) {
    return null;
  }

  return (
    <group renderOrder={getRenderOrder(Object3dNames.ExternalDataTrenchSegments)}>
      {trenches.map(({ id, points, type }) => (
        <LineString key={id} layingType={type} points={points.flat()} />
      ))}
    </group>
  );
};

export const ExternalData = () => {
  const { isExternalVisible } = useExternalDataState();
  const { invalidate } = useThree();

  useEffect(() => {
    invalidate();
  }, [isExternalVisible, invalidate]);

  // Avoid data fetching in case the component is not visible
  return isExternalVisible ? <ExternalDataTrenches /> : null;
};
